/**
 * ABOUT THIS PIECE - the byline, the reading time and the way to the guidelines (register R-033,
 * lens finding C2-09).
 *
 * THREE SMALL THINGS THE EDITOR DID NOT HAVE, each from where it already lives:
 *   - THE BYLINE is the author's own profile, `getOwnProfile` - name, address, headline, picture -
 *     so the author sees the card readers will see beside the piece. The profile also carries its
 *     private block (contact e-mail, rate); nothing of it is drawn here.
 *   - THE READING TIME is computed from THE word count - the shared definition the checklist and
 *     the submit guard use (features/cabinet/api/editor-measure.ts) - so the two numbers on this
 *     screen cannot disagree. The rate is ours: a writing statistic computed in the browser is a
 *     client concern (09-authors-app.md, "Writing stats ... CLIENT CONCERN"), and no ruling names
 *     one. 200 words a minute is the slow end of the usual range, so the estimate does not flatter.
 *   - THE GUIDELINES are on the Support page's guide cards and nowhere else in the cabinet; the
 *     editor links there rather than restating them. (Moving there with a change not yet saved
 *     asks first - ./use-leave-warning.ts.)
 *
 * The profile read failing takes nothing else down: the card says so and offers to read again.
 */
import { Avatar, Button, Group, Stack, Text, Title } from '@mantine/core';
import { useCallback } from 'react';
import { articleWordCount } from '../../../features/cabinet/api/editor-measure';
import { formatCount } from '../../../features/cabinet/format';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import { useAsync } from '../../../features/cabinet/state';
import { InAppAnchor } from '../../../features/shell/InAppAnchor';
import { useServedImageSrc } from '../../../features/storage/emulator-image-url';
import type { OwnProfile } from '../../../features/cabinet/api/editor-api';
import { Trans, useTranslation } from '../../../i18n';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import type { EditorDraft } from './document';

/** Words a minute. See the header: ours, and deliberately the slow end. */
export const READING_WORDS_PER_MINUTE = 200;

/**
 * Chinese and Japanese count every character as a word (`text-measure.ts`), and a reader takes in
 * about 500 characters a minute, so 200 would make their reading time two and a half times too long.
 */
export const READING_CJK_CHARACTERS_PER_MINUTE = 500;

/** The rate for an article written in `language`. */
export function readingRate(language?: string | null): number {
  return language === 'ja' || language === 'zh'
    ? READING_CJK_CHARACTERS_PER_MINUTE
    : READING_WORDS_PER_MINUTE;
}

/** Whole minutes, rounded UP so a 250-word piece is "2 minutes" and not "1". Zero words is zero. */
export function readingMinutes(words: number, language?: string | null): number {
  return words <= 0 ? 0 : Math.max(1, Math.ceil(words / readingRate(language)));
}

/** The sentence under "Reading time", its count in the cabinet's language (design 24, T16). */
export function readingTimeSentence(
  words: number,
  t: ArticlesT = articlesT(),
  language?: string | null,
): string {
  const minutes = readingMinutes(words, language);
  if (minutes === 0) {
    return t('editor.about.nothingToRead');
  }
  return t('editor.about.readingTime', {
    minutes: t('editor.about.minutes', { count: minutes }),
    words: t('common.words', { count: words, value: formatCount(words) }),
    rate: String(readingRate(language)),
  });
}

/** Where the guide cards are. The Support page's own path (routes/support/routes.tsx). */
const GUIDES_HREF = '/support';

export interface AboutThisPieceProps {
  readonly draft: EditorDraft;
}

export function AboutThisPiece({ draft }: AboutThisPieceProps) {
  const { t } = useTranslation('articles');
  const api = useEditorApi();
  const profile = useAsync(
    useCallback((signal: AbortSignal) => api.getOwnProfile(signal), [api]),
    'editor:own-profile',
  );
  const words = articleWordCount(draft);

  return (
    <Stack component="section" gap="md" aria-labelledby="editor-about-title">
      <Title id="editor-about-title" order={2} size="h5">
        {t('common.aboutThisArticle')}
      </Title>

      <Stack gap={6}>
        <Text size="sm" fw={700} component="h3" m={0}>
          {t('editor.about.byline')}
        </Text>
        <Byline profile={profile.data} status={profile.status} onRetry={profile.refetch} />
      </Stack>

      <Stack gap={4}>
        <Text size="sm" fw={700} component="h3" m={0}>
          {t('editor.about.readingTimeTitle')}
        </Text>
        <Text size="sm">{readingTimeSentence(words, t, draft.sourceLanguage)}</Text>
      </Stack>

      <Stack gap={4}>
        <Text size="sm" fw={700} component="h3" m={0}>
          {t('editor.about.guidelines')}
        </Text>
        <Text size="sm">
          {/* The link's words are the catalogue's, put in its place by `Trans`. */}
          <Trans
            t={t}
            i18nKey="editor.about.guides"
            components={{
              anchor: (
                <InAppAnchor href={GUIDES_HREF} size="sm">
                  {null}
                </InAppAnchor>
              ),
            }}
          />
        </Text>
      </Stack>
    </Stack>
  );
}

function Byline({
  profile,
  status,
  onRetry,
}: {
  readonly profile: OwnProfile | undefined;
  readonly status: 'loading' | 'ready' | 'error';
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation('articles');
  const avatar = useServedImageSrc(profile?.avatarUrl ?? null);
  if (profile === undefined) {
    return status === 'error' ? (
      <Group gap="sm" wrap="wrap">
        <Text size="sm" c="dimmed">
          {t('editor.about.profileFailed')}
        </Text>
        <Button size="xs" variant="default" onClick={onRetry}>
          {t('common.tryAgain')}
        </Button>
      </Group>
    ) : (
      <Text size="sm" c="dimmed">
        {t('editor.about.readingProfile')}
      </Text>
    );
  }
  const name = profile.displayName?.trim() ?? '';
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      {/* The name is beside it, so the picture says nothing a reader needs: `alt=""`. */}
      <Avatar
        src={avatar.src}
        imageProps={{ onError: avatar.onError }}
        alt=""
        radius="xl"
        size={48}
      >
        {name.slice(0, 1).toUpperCase() || '?'}
      </Avatar>
      <Stack gap={0}>
        <Text size="sm" fw={600}>
          {name.length === 0 ? t('editor.about.noName') : name}
        </Text>
        {profile.slug === null || profile.slug === undefined ? null : (
          <Text size="xs" c="dimmed">
            @{profile.slug}
          </Text>
        )}
        {profile.headline === null || profile.headline === undefined ? null : (
          <Text size="xs">{profile.headline}</Text>
        )}
      </Stack>
    </Group>
  );
}
